import React from "react";
import { useController } from "react-hook-form";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import { usePrefixedFieldName } from "shared/components/form/FormFieldPrefix";
import { useIsFormDisabled } from "shared/components/form/FormStateProvider";
import { DEFAULT_DECIMAL_SCALE } from "shared/components/input/InputConstants";
import { makeInputTransforms } from "shared/components/input/text/InputUtils";
import TextInput, {
  TextInputProps,
} from "shared/components/input/text/TextInput";
import { HashIcon } from "shared/icons/HashIcon";
import { PercentIcon } from "shared/icons/PercentIcon";
import { FormInputValue } from "shared/types/FormInputValue";
import { getFieldError } from "shared/utils/FormUtils";

export type NumberInputValueType = "number" | "percent";

export type NumberInputProps = Omit<
  NumericFormatProps<TextInputProps>,
  "type" | "leftAddon" | "rightAddon"
> & {
  valueType?: NumberInputValueType;
  hideIcon?: boolean;
};

export type FormNumberInputProps = Omit<
  NumberInputProps,
  "name" | "value" | "error" | "onChangeValue" | "onChange" | "type"
> & {
  name: string;
};

const leftAddons: Partial<Record<NumberInputValueType, React.ReactNode>> = {
  number: <HashIcon color="gray.400" />,
};
const rightAddons: Partial<Record<NumberInputValueType, React.ReactNode>> = {
  percent: <PercentIcon width="24px" height="24px" color="gray.400" />,
};

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      onChangeValue,
      allowNegative = false,
      hideIcon,
      valueType = "number",
      decimalScale = DEFAULT_DECIMAL_SCALE,
      ...props
    },
    ref,
  ) => (
    <NumericFormat<TextInputProps>
      customInput={TextInput}
      thousandSeparator={true}
      getInputRef={ref}
      valueIsNumericString={true}
      decimalScale={decimalScale}
      type="text"
      inputMode="decimal"
      onValueChange={({ value }) => {
        onChangeValue?.(value);
      }}
      allowNegative={allowNegative}
      leftElement={hideIcon ? undefined : leftAddons[valueType]}
      rightElement={hideIcon ? undefined : rightAddons[valueType]}
      min={valueType === "percent" ? 0 : undefined}
      max={valueType === "percent" ? 100 : undefined}
      {...props}
    />
  ),
);
NumberInput.displayName = "NumberInput";

export const FormNumberInput: React.FC<FormNumberInputProps> = ({
  name,
  isDisabled,
  ...otherProps
}) => {
  const prefixedName = usePrefixedFieldName(name);
  const {
    field: { value, onChange, ...field },
    formState: { errors },
  } = useController<{ text: FormInputValue }>({
    name: prefixedName as "text",
    shouldUnregister: true,
    defaultValue: "",
  });
  const isFormDisabled = useIsFormDisabled();
  const { transformInput, transformOutput } = makeInputTransforms(
    typeof value === "number",
    otherProps.valueType === "percent",
    true,
  );
  return (
    <NumberInput
      {...field}
      {...otherProps}
      value={transformInput(value)}
      onChangeValue={(value) => {
        onChange(transformOutput(value));
      }}
      isDisabled={isDisabled || isFormDisabled}
      error={getFieldError(errors, prefixedName)?.message}
    />
  );
};

export default TextInput;
